import {ContentEditableTextEditor, createContentEditableTextEditor} from '@frontapp/editor-html';
import {debounce} from 'lodash';
import React, {Component, createRef} from 'react';
import styled, {css} from 'styled-components';

import {KnowledgeBaseInsertHandler} from '../../../../libs/knowledge-base/shared/components/composerIntegration/knowledgeBaseComposerDropdown';
import {buildPublicArticleUrl} from '../../../../libs/knowledge-base/shared/helpers/urlHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {UploadedAttachmentModel} from '../../../../libs/shared/core/src/models/api/attachmentModel';
import {ChannelModel} from '../../../../libs/shared/core/src/models/api/channelModel';
import {CompanyModel} from '../../../../libs/shared/core/src/models/api/company/companyModel';
import {MessageErrorModel} from '../../../../libs/shared/core/src/models/api/messageErrorModel';
import {DraftMessageModel} from '../../../../libs/shared/core/src/models/api/messageModel';
import {PushProps, withPush} from '../../../../libs/shared/core/src/services/push/pushContext';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {
  growToFitHeightContainerStyle,
  growToFitHeightStyle,
} from '../../../../libs/shared/ui/src/styles/styleHelpers';
import {
  PrecogProps,
  PrecogUpdateTypesEnum,
  withMessagePrecog,
} from '../../../../srcCommon/inboxes/composer/precogs/precogHooks';
import {htmlToText} from '../../../../srcCommon/services/converters/htmlToTextConverter';
import {IntegrationFileAttachment} from '../../../helpers/integrationHelpers';
import {
  findDraftValidationErrors,
  isDraftValid,
  SendDraftErrorsEnum,
} from '../../../helpers/models/messageHelpers';
import {MessagePatch} from '../../../helpers/models/messagePatchHelpers';
import {AnswerModel} from '../../../models/api/answerModel';
import {SchedulingLinkModel} from '../../../models/api/schedulingLinkModel';
import {MessageComposerApi, registerMessageComposer} from '../../../services/composers';
import {baseEmojiValidationMap} from '../../../services/emojis/emojis';
import {VariableInterpolator} from '../../../services/variableInterpolation/variableInterpolator';
import {DraggedAttachmentItemsModel} from '../../common/attachments/attachmentsDragAndDrop';
import {AttachmentsEditorProps} from '../../common/attachments/attachmentsEditor';
import {ChangeManager} from '../../common/editor/change/changeManager';
import {TextEditorChangeManager} from '../../common/editor/change/textEditorChangeManager';
import {ContentEditable, EditorTypesEnum} from '../../common/editor/contentEditable';
import {EmojiEditorDropdown} from '../../common/editor/dropdowns/emojiEditorDropdown';
import {QuickInsertContainer} from '../../common/editor/dropdowns/quickInsert/quickInsertContainer';
import {
  createEmojiPlugin,
  EmojiPluginRenderModesEnum,
  insertEmojiInEditor,
} from '../../common/editor/plugins/text/emojiPlugin';
import {createMentionPlugin} from '../../common/editor/plugins/text/mentionPlugin';
import {ServiceSubscription} from '../../common/editor/serviceSubscription';
import {ComposerBannerContainer} from './banner/composerBannerContainer';
import {ChannelSaver} from './channelPicker/pickChannelDropdownContainer';
import {ComposerAttachments} from './composerAttachments';
import {ComposerBase} from './composerBase';
import {SendMessage} from './toolbar/buttons/sendToolbarButton';
import {FullComposerToolbar, FullComposerToolbarProps} from './toolbar/fullComposerToolbar';
import {TweetComposerCounter} from './tweetComposerCounter';
import {TweetComposerHeader, TweetComposerHeaderProps} from './tweetComposerHeader';

/*
 * Props.
 */

export interface TweetComposerPublicProps
  extends Omit<
      FullComposerToolbarProps,
      'onIntegrationAttachLinks' | 'editorServices' | 'channelId' | 'isMarkdownEditor'
    >,
    Omit<TweetComposerHeaderProps, 'panelId'>,
    AttachmentsEditorProps {
  channel: ChannelModel;
  company: CompanyModel;
  message: DraftMessageModel;
  saveError?: MessageErrorModel;
  attemptedSendDraftAttachments: boolean;
  dismissAttemptedSendDraftAttachments: () => void;
  reloadMessage: () => void;
  saveChannel: ChannelSaver;
  saveMessageAsync: (patch: MessagePatch) => void;
  interpolator: VariableInterpolator;
  addAttachment: (model: UploadedAttachmentModel) => void;
  onDropAttachmentItems: (model: DraggedAttachmentItemsModel) => void;
  dontFocusComposer?: boolean;
  isComposerPopup: boolean;
  onPaste: () => void;
  isComposerPopout: boolean;
  hasTwitterOauth2: boolean;
}

type TweetComposerProps = TweetComposerPublicProps & PrecogProps & PushProps;

/*
 * Style.
 */

const mentionClassName = 'front-decoration-mention';

const editorStyle = css`
  padding: 15px 16px;
  line-height: 20px;
`;

const StyledEditorDiv = styled.div`
  ${growToFitHeightStyle};
  display: grid;
  grid-template-columns: 1fr auto;
  grid-auto-rows: 100%;
`;

const StyledContentEditableWrapperDiv = styled.div`
  ${growToFitHeightStyle};
  ${growToFitHeightContainerStyle};
  min-width: 0;
  /* Allow more initial room for composing */
  min-height: 80px;
`;

const StyledContentEditable = styled(ContentEditable)`
  ${growToFitHeightStyle};
  ${editorStyle};

  .${mentionClassName} {
    color: ${(p) => p.theme.interactiveText};
    font-weight: ${fontWeights.medium};
  }
`;

const StyledPlaceholderOverlayDiv = styled.div`
  position: absolute;
  pointer-events: none;
  ${editorStyle};
`;

const StyledPlaceholderDiv = styled.div`
  color: ${(p) => p.theme.composerPlaceholder};
`;

/*
 * Component.
 */

interface TweetComposerState {
  cannedAnswerId?: number;
}

class TweetComposerInternal
  extends Component<TweetComposerProps, TweetComposerState>
  implements MessageComposerApi
{
  constructor(props: TweetComposerProps) {
    super(props);

    const {text} = props.message;

    this.state = {
      cannedAnswerId: props.message.cannedAnswerId,
    };
    this.editor = createEditor(text);
    this.changeManager = new TextEditorChangeManager(this.editor);
    this.wasTextSaved = false;
  }

  private readonly editor: ContentEditableTextEditor;
  private readonly changeManager: ChangeManager;
  private unregisterComposer?: () => void;
  private readonly editorWrapperRef = createRef<HTMLDivElement>();

  /*
   * Saving.
   */

  private wasTextSaved: boolean;
  private readonly saveTextDebounced = debounce(() => this.saveText(), 1000);

  private saveText() {
    this.wasTextSaved = true;
    this.saveTextDebounced.cancel();

    const {text} = this.editor.services.text;
    const newCannedAnswerId = this.state.cannedAnswerId;

    this.props.saveMessageAsync({
      newText: text,
      newCannedAnswerId,
    });
  }

  /*
   * Event handlers.
   */

  private readonly onEditorBlur: () => void = () => {
    // Flush any pending changes when an editor loses focus.
    this.saveTextDebounced.flush();
  };

  private readonly onAnswerSelect: (model: AnswerModel) => void = (answer) => {
    if (answer.attachments.length) {
      answer.attachments.forEach((attachment) => {
        this.props.addAttachment({
          ...attachment,
          // Convert inline attachments in canned response to regular attachments.
          isInline: false,
        });
      });
    }

    const {interpolator} = this.props;
    const answerText = interpolator.interpolateText(htmlToText(answer.body));
    this.insertText(answerText);

    // The canned answer id is needed for analytics, but the message we send is formatted on the front end.
    // We do not store this canned answer id to the store, it is only used for this editor session.
    this.setCannedAnswerId(answer.id);
  };

  private readonly onLinkSelect: (model: SchedulingLinkModel) => void = (link) => {
    this.insertText(link.bookingPageUrl);
  };

  private readonly onKnowledgeBaseArticleSelect: KnowledgeBaseInsertHandler = ({
    article,
    category,
    locale,
    knowledgeBase,
    headerText,
  }) => {
    if (article) {
      this.insertText(buildPublicArticleUrl(knowledgeBase, article.id, locale, headerText));
    } else if (category && category.url) {
      this.insertText(category.url);
    }
  };

  private readonly onEmojiSelect: (arg: string) => void = (shortcode) => {
    insertEmojiInEditor(this.editor, shortcode);
  };

  private readonly onIntegrationAttachLink: (arg: ReadonlyArray<IntegrationFileAttachment>) => void = (
    attachments,
  ) => {
    const text = attachments.map((a) => a.url).join('');
    this.insertText(text);
  };

  private readonly sendMessage: SendMessage = (options) => {
    // Make sure the draft is valid before sending.
    const {message, channel, hasSendPermission, company} = this.props;
    const {text} = this.editor.services.text;
    if (!isDraftValid(message, channel, company, {text}) || !hasSendPermission) {
      return;
    }

    if (!this.wasTextSaved) {
      this.saveTextDebounced();
    }

    this.saveTextDebounced.flush();

    this.props.sendMessage(options);
  };

  private readonly deleteMessage = () => {
    this.wasTextSaved = true;
    this.saveTextDebounced.cancel();
    this.props.deleteMessage();
  };

  private readonly setCannedAnswerId = (cannedAnswerId: number) => {
    this.setState({cannedAnswerId});
  };

  /*
   * Lifecycle.
   */

  componentDidMount() {
    this.changeManager.subscribe((isDirty) => {
      // Avoid saving a previously unsaved draft when a non-UI change was made.
      if (!this.wasTextSaved && !isDirty) {
        return;
      }

      this.saveTextDebounced();
      this.props.updatePrecog(PrecogUpdateTypesEnum.CONTINUE);
    });

    this.registerComposer();
  }

  componentDidUpdate(previousProps: Readonly<TweetComposerProps>) {
    if (this.props.message.uid !== previousProps.message.uid) {
      throw new Error('TweetComposer must be keyed by message uid');
    }
  }

  private registerComposer() {
    if (this.unregisterComposer) {
      this.unregisterComposer();
    }

    this.unregisterComposer = registerMessageComposer(this.props.message.uid, this);
  }

  componentWillUnmount() {
    if (this.unregisterComposer) {
      this.unregisterComposer();
    }

    this.changeManager.unsubscribe();
  }

  /*
   * API.
   */

  focus() {
    this.editor.services.focus.focus();
  }

  private insertText(text: string) {
    this.editor.services.edit.insertText(text);
    this.focus();
  }

  insertHtml(html: string) {
    this.insertText(htmlToText(html));
  }

  // eslint-disable-next-line class-methods-use-this
  insertSubject(_value: string) {
    // Noop for us.
  }

  saveMessage = (messagePatch: MessagePatch) => {
    this.props.saveMessageAsync(messagePatch);
  };

  private setText(text: string) {
    this.editor.services.text.setText(text);
    this.focus();
  }

  setHtml(html: string) {
    this.setText(htmlToText(html));
  }

  // eslint-disable-next-line class-methods-use-this
  setSubject(_value: string) {
    // Noop for us.
  }

  /*
   * Render.
   */

  render() {
    // TODO: See if we need to debounce this more, since we're doing htmlToText on every change here.
    return (
      <ServiceSubscription source={this.editor.services.text}>
        {({text}) => this.renderComposer(text)}
      </ServiceSubscription>
    );
  }

  private renderComposer(text: string) {
    const {message, channel, onFileUpload, onDropAttachmentItems, company} = this.props;
    const validationErrors = findDraftValidationErrors(message, channel, company, {text});

    return (
      <ComposerBase
        header={this.renderHeader()}
        editor={this.renderEditor(text)}
        toolbar={(isFocused) => this.renderToolbar(validationErrors, isFocused)}
        onDropFile={(file) => onFileUpload(file)}
        onDropAttachmentItems={onDropAttachmentItems}
        isComposerPopout={this.props.isComposerPopout}
        shouldRenderInlineGrammarlyButton
      />
    );
  }

  private renderHeader() {
    return <TweetComposerHeader {...this.props} />;
  }

  private renderEditor(text: string) {
    return (
      <>
        <ComposerAttachments {...this.props} />
        <StyledEditorDiv>
          {this.renderBodyEditor()}
          <TweetComposerCounter text={text} channel={this.props.channel} />
        </StyledEditorDiv>
        <EmojiEditorDropdown autocompleteService={this.editor.services.autocomplete} />
      </>
    );
  }

  private renderBodyEditor() {
    const {editor} = this;
    const {dontFocusComposer} = this.props;
    const shouldFocus = !dontFocusComposer;

    return (
      <>
        <ServiceSubscription source={editor.services.empty}>
          {({isEmpty}) => isEmpty && renderPlaceholderOverlay()}
        </ServiceSubscription>
        <StyledContentEditableWrapperDiv ref={this.editorWrapperRef}>
          <StyledContentEditable
            editor={editor}
            shouldFocus={shouldFocus}
            onBlur={this.onEditorBlur}
            onPaste={this.props.onPaste}
            hasGrammarlySupport
            editorType={EditorTypesEnum.TWEET}
          />
          {this.renderQuickInsertDropdown()}
        </StyledContentEditableWrapperDiv>
      </>
    );
  }

  private renderQuickInsertDropdown() {
    return (
      <QuickInsertContainer
        panelId={this.props.panelId}
        channelId={this.props.channel.id}
        conversation={this.props.conversation}
        editorServices={this.editor.services}
        editorWrapperRef={this.editorWrapperRef}
        interpolator={this.props.interpolator}
        message={this.props.message}
        namespace={this.props.namespace}
        companyUrl={this.props.company.url}
        onAnswerSelect={this.onAnswerSelect}
        onSchedulingLinkInsertLink={this.onLinkSelect}
        onKnowledgeBaseInsertContent={this.onKnowledgeBaseArticleSelect}
      />
    );
  }

  private renderToolbar(validationErrors: ReadonlyArray<SendDraftErrorsEnum>, isFocused: boolean) {
    const {
      message,
      channel,
      reloadMessage,
      saveError,
      attemptedSendDraftAttachments,
      dismissAttemptedSendDraftAttachments,
      hasTwitterOauth2,
    } = this.props;
    const hasValidationErrors = validationErrors.length > 0 || Boolean(message.error);

    return (
      <>
        <ComposerBannerContainer
          validationErrors={validationErrors}
          conversation={this.props.conversation}
          message={message}
          channel={channel}
          saveMessage={() => this.saveMessage({})}
          reloadMessage={reloadMessage}
          saveError={saveError}
          attemptedSendDraftAttachments={attemptedSendDraftAttachments}
          dismissAttemptedSendDraftAttachments={dismissAttemptedSendDraftAttachments}
          isPopup={this.props.isComposerPopup}
        />
        <FullComposerToolbar
          {...this.props}
          hasValidationErrors={hasValidationErrors}
          areSendShortcutsDisabled={!isFocused}
          onEmojiSelect={this.onEmojiSelect}
          sendMessage={this.sendMessage}
          deleteMessage={this.deleteMessage}
          onAnswerSelect={this.onAnswerSelect}
          onIntegrationAttachLinks={this.onIntegrationAttachLink}
          composerEntryPoints={this.props.composerEntryPoints}
          editorServices={this.editor.services}
          shouldHideAttachmentsButton={hasTwitterOauth2}
          onKnowledgeBaseInsertContent={this.onKnowledgeBaseArticleSelect}
          hasSaveErrorMessage={Boolean(saveError?.description)}
          channel={this.props.channel}
          isMarkdownEditor={false}
        />
      </>
    );
  }
}

function renderPlaceholderOverlay() {
  return <StyledPlaceholderOverlayDiv>{renderPlaceholder()}</StyledPlaceholderOverlayDiv>;
}

function renderPlaceholder() {
  return (
    <StyledPlaceholderDiv>
      <Trans id="composer-tweetplaceholder">Type your tweet</Trans>
    </StyledPlaceholderDiv>
  );
}

/*
 * Helpers.
 */

function createEditor(initialText = '') {
  const editor = createContentEditableTextEditor({
    plugins: [
      createEmojiPlugin({
        renderMode: EmojiPluginRenderModesEnum.UNIFIED,
        emojiValidationMap: baseEmojiValidationMap,
      }),
      createMentionPlugin({className: mentionClassName}),
    ],
  });
  editor.services.text.setText(initialText);
  return editor;
}

export const TweetComposer = withPush(withMessagePrecog(TweetComposerInternal));
